import {
  FIXED_PRODUCT_IDS,
  FORM_IDS,
} from '@bulknutrients/bulk-shared-lib/constants';
import {
  type CustomForm,
  isPortableText,
} from '@bulknutrients/bulk-shared-lib/interfaces/IForms';
import { getFormById } from '@bulknutrients/bulk-shared-lib/lib/queries/sanity/forms';
import { cdnClient } from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Container';
import { RequestSampleCreateAccountForm } from '@components/CreateAccountForm/RequestSampleCreateAccountForm';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import type { GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { ReactElement } from 'react';

import { stockLevelHandlerFetch } from '../api/stock-level';

export default function RequestFreeSamplePage({
  formData,
  freeSampleStock,
}: {
  formData: CustomForm;
  freeSampleStock: Record<string, boolean>;
}): ReactElement {
  const router = useRouter();
  const requestSampleSource = (router.query.source as string) ?? 'standard';

  return (
    <>
      <Head>
        <title>Free Protein Powder Samples Australia | Bulk Nutrients</title>
        <meta
          name="title"
          content="Free Protein Powder Samples Australia | Bulk Nutrients"
        />
        <meta
          name="description"
          content="Try before you buy with free protein samples from Bulk Nutrients. Choose from aminos, pre workouts, protein powders and more. Grab your freebie now!"
        />
        <meta name="robots" content="noindex" />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/free-samples`}
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Free Samples', path: '/free-samples' },
          ]}
        />
      </Container>
      <Container
        as="main"
        id="main-content"
        className="mx-auto my-0 max-w-narrow-page"
      >
        <article>
          <h1>Bulk Nutrients Free Samples</h1>
          <Paragraph>
            What's better than Bulk Nutrients protein? Free protein, of course!
          </Paragraph>
          <Paragraph>
            If you're a new customer in Australia looking to try us for the
            first time, request a sample below and we'll have it on your
            doorstep within 10-14 days... plus create you an account!
          </Paragraph>
          <Paragraph>
            And, if you love it - having the account will make it easier to jump
            in and buy the full size!
          </Paragraph>
          <RequestSampleCreateAccountForm
            requestSampleForm={formData}
            sampleStock={freeSampleStock}
            source={requestSampleSource}
          />
          <Paragraph>
            Note that our Free Samples are sent via mail (regular letter), this
            can take up to 10-14 days to arrive in your mailbox and are limited
            to Australian residences.
          </Paragraph>
        </article>
      </Container>
    </>
  );
}

RequestFreeSamplePage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticProps: GetStaticProps = async ({
  params,
  preview,
  previewData,
}) => {
  const formData = await cdnClient.fetch<CustomForm>(getFormById, {
    id: FORM_IDS.FREE_SAMPLE,
  });

  const freeSampleStock = await stockLevelHandlerFetch(
    FIXED_PRODUCT_IDS.freeSample.toString()
  );

  // Go into the form data and filter out the samples that are out of stock
  // Get input index of requested_sample input
  const requestedSampleIndex = formData.fieldsets
    .flatMap((f) => f.inputs)
    .findIndex(
      (input) => !isPortableText(input) && input.name === 'requested_sample'
    );

  // Get the different options that are listed in Sanity
  const sampleOptions = formData.fieldsets.flatMap((f) => f.inputs)[
    requestedSampleIndex
  ];

  // We have to iterate and filter out any that are out of stock
  if ('options' in sampleOptions) {
    sampleOptions.options = sampleOptions.options.filter((option) => {
      if (freeSampleStock[option.value as string]) return true;
      return false;
    });
    formData.fieldsets.flatMap((f) => f.inputs)[requestedSampleIndex] =
      sampleOptions;
  }

  return { props: { formData, freeSampleStock }, revalidate: 3600 };
};
